import { BaseComponent } from '../../components/base-component'
import { $el, $list, $elParent } from '../../utils/utils'

export class SizeGuideType extends BaseComponent {
  private ageTabs: HTMLElement[]
  private ageContainers: HTMLElement[]
  private fitTabs: HTMLElement[]
  private fitContainers: HTMLElement[]
  private unitToggle: HTMLInputElement | null
  private tableCells: HTMLTableCellElement[]
  private isStaticPage: boolean
  private actionLinks: HTMLElement[]

  connectedCallback() {
    super.connectedCallback()
    this.isStaticPage = this.hasAttribute('data-static-page')
    
    // Only initialize size guide elements if not a static page
    if (!this.isStaticPage) {
      this.initializeElements()
      this.initializeListeners()
    }

    // Handle initial hash if present
    this.handleUrlHash()
  }

  private initializeElements() {
    this.ageTabs = $list('[data-size-guide-age-target]', this)
    this.ageContainers = $list('[data-size-guide-age-tab]', this)
    this.fitTabs = $list('[data-size-guide-fit-target]', this)
    this.fitContainers = $list('[data-size-guide-fit-tab]', this)
    this.unitToggle = $el('.size-guide-type__unit-toggle-input', this)
    this.tableCells = $list('td:not(:first-child)', this)
    this.actionLinks = $list('.size-guide-type__action-link', this)
  }

  private initializeListeners() {
    this.ageTabs.forEach(tab => {
      tab.addEventListener('click', this.handleAgeTabClick.bind(this))
    })

    this.fitTabs.forEach(tab => {
      tab.addEventListener('click', this.handleFitTabClick.bind(this))
    })

    this.unitToggle?.addEventListener('change', this.handleUnitToggle.bind(this))

    this.actionLinks.forEach(link => {
      link.addEventListener('click', this.handleActionLinkClick.bind(this))
    })

    // Add hash change listener
    window.addEventListener('hashchange', this.handleUrlHash.bind(this))
  }

  private handleAgeTabClick(event: Event) {
    const tab = event.currentTarget as HTMLElement
    const targetId = tab.dataset.sizeGuideAgeTarget

    // Update age tab active states
    this.ageTabs.forEach(ageTab => {
      ageTab.classList.toggle('size-guide-type__age-tab--active', ageTab === tab)
    })

    // Update age container visibility
    this.ageContainers.forEach(container => {
      container.classList.toggle('size-guide-age--active', container.dataset.sizeGuideAgeTab === targetId)
    })
  }

  private handleFitTabClick(event: Event) {
    const tab = event.currentTarget as HTMLElement
    const targetId = tab.dataset.sizeGuideFitTarget

    // Update fit tab active states within the current age section
    const ageSection = $elParent('.size-guide-age', tab)
    if (!ageSection) return

    const fitTabsInSection = $list('[data-size-guide-fit-target]', ageSection)
    fitTabsInSection.forEach(fitTab => {
      fitTab.classList.toggle('size-guide-age__fit-tab-link--active', fitTab === tab)
    })

    // Update fit container visibility
    const fitContainersInSection = $list('[data-size-guide-fit-tab]', ageSection)
    fitContainersInSection.forEach(container => {
      container.classList.toggle('size-guide-fit--active', container.dataset.sizeGuideFitTab === targetId)
    })
  }

  private handleUnitToggle(event: Event) {
    const isCm = (event.target as HTMLInputElement).checked
    
    // Get all size guide types and their table cells
    const allSizeGuideTypes = document.querySelectorAll('size-guide-type') as NodeListOf<HTMLElement>
    allSizeGuideTypes.forEach(sizeGuideType => {
      const cells = $list('td:not(:first-child)', sizeGuideType)
      cells.forEach(cell => {
        const value = cell.textContent?.trim()
        if (!value) return

        if (value.includes('-')) {
          // Handle range values
          const [start, end] = value.split('-').map(v => v.trim())
          cell.textContent = isCm 
            ? `${this.inchToCm(parseFloat(start))} - ${this.inchToCm(parseFloat(end))}` 
            : `${this.cmToInch(parseFloat(start))} - ${this.cmToInch(parseFloat(end))}`
        } else {
          // Handle single values
          cell.textContent = isCm 
            ? `${this.inchToCm(parseFloat(value))}` 
            : `${this.cmToInch(parseFloat(value))}`
        }
      })
    })

    // Sync all unit toggles to the same state
    const allUnitToggles = document.querySelectorAll('.size-guide-type__unit-toggle-input') as NodeListOf<HTMLInputElement>
    allUnitToggles.forEach(toggle => {
      toggle.checked = isCm
    })
  }

  private inchToCm(inches: number): string {
    return Math.round(inches * 2.54).toString()
  }

  private cmToInch(cm: number): string {
    return Math.round(cm / 2.54).toString()
  }

  private handleActionLinkClick(event: Event) {
    const link = event.currentTarget as HTMLElement
    const targetId = link.dataset.sizeGuideTarget
    const linkText = link.textContent?.trim()
    
    // Find all size guide types
    const sizeGuideTypes = document.querySelectorAll('size-guide-type') as NodeListOf<HTMLElement>
    
    // Update size guide type visibility
    sizeGuideTypes.forEach(type => {
      type.classList.toggle('size-guide-type--active', type.dataset.id === targetId)
    })
    
    // Update navigation active states and button text
    const navigationLinks = document.querySelectorAll('.size-guide-navigation__link') as NodeListOf<HTMLElement>
    const buttonText = document.querySelector('.size-guide-navigation__button-text')
    
    navigationLinks.forEach(navLink => {
      const isActive = navLink.dataset.sizeGuideTarget === targetId
      navLink.classList.toggle('size-guide-navigation__link--active', isActive)
      navLink.classList.toggle('size-guide-navigation__link--selected', isActive)
      
      // Update button text if this is the active link
      if (isActive && buttonText && linkText) {
        buttonText.textContent = linkText
      }
    })
  }

  private handleUrlHash() {
    // Get everything after # while handling potential query parameters
    const hashIndex = window.location.href.indexOf('#')
    if (hashIndex === -1) return
    
    const hash = window.location.href.slice(hashIndex + 1).split('?')[0]
    if (!hash) return

    // Find the navigation link with matching target
    const targetLink = document.querySelector(`.size-guide-navigation__link[data-size-guide-target="${hash}"]`) as HTMLElement
    if (targetLink) {
      // Simulate a click on the target link
      targetLink.click()
    }
  }
}